// src/component/AdminPanelOrders.jsx
import React, { useEffect, useMemo, useState } from "react";
import AdminPanelOrdersDetails from "./AdminPanelOrdersDetails";
import { useAdminApi } from "./adminApi";

/* ---------- Утилиты UI ---------- */
const Spinner = ({ className = "h-4 w-4" }) => (
  <span
    className={`inline-block ${className} border-2 border-black/20 border-t-black rounded-full animate-spin`}
    aria-hidden="true"
  />
);
const StatusBadge = ({ status }) => {
  const raw = (status?.code ?? status?.title ?? "").toString();
  if (!raw.trim()) {
    return (
      <span className="px-2 py-0.5 rounded-full text-xs ring-1 ring-gray-200 bg-gray-50 text-gray-700">
        —
      </span>
    );
  }

  const s = raw.trim().toLowerCase().replace(/ё/g, "е");

  // «Завершён» и аналоги → зелёный
  const isGreen =
    /(заверш|готов|выдан|выполн|доставлен)/.test(s) ||
    /(completed|complete|done|finished|closed|success|delivered)/.test(s);

  // базовые цвета для прочих статусов (как было)
  const map = {
    new:        "bg-blue-50  text-blue-700  ring-blue-200",
    pending:    "bg-amber-50 text-amber-700 ring-amber-200",
    processing: "bg-indigo-50 text-indigo-700 ring-indigo-200",
    paid:       "bg-emerald-50 text-emerald-700 ring-emerald-200",
    shipped:    "bg-cyan-50 text-cyan-700 ring-cyan-200",
    delivered:  "bg-green-50 text-green-700 ring-green-200",
    canceled:   "bg-rose-50 text-rose-700 ring-rose-200",
    cancelled:  "bg-rose-50 text-rose-700 ring-rose-200",
    refunded:   "bg-gray-100 text-gray-700 ring-gray-300",
  };

  // если распознали «завершён» — принудительно зелёный
  const cls = isGreen
    ? "bg-green-50 text-green-700 ring-green-200"
    : (map[s] || "bg-gray-50 text-gray-700 ring-gray-200");

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ring-1 ${cls}`}>
      {status?.title || status?.code || raw}
    </span>
  );
};


/* ---------- Дата создания как в Details ---------- */
// берём created_date или created_at, как в AdminPanelOrdersDetails
const getCreated = (o) => o?.created_date || o?.created_at || null;
// формат как в Details
const fmtDate = (d) => {
  try {
    // Убираем микросекунды и добавляем "Z" для корректного распознавания
    const formattedDate = d.replace(/\.(\d{6})$/, '');
    const date = new Date(formattedDate + 'Z');
    if (isNaN(date)) {
      return "—";
    }
    return date.toLocaleString("ru-RU");
  } catch (e) {
    return "—";
  }
};


const sumSafe = (arr, f) => arr.reduce((acc, x) => acc + (Number(f(x)) || 0), 0);

export default function AdminPanelOrders() {
  const api = useAdminApi();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedOrder, setSelectedOrder] = useState(null);

  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  /* ---------- Загрузка ---------- */
  const loadOrders = async () => {
    setLoading(true);
    try {
      const { data, status } = await api.get('admin/orders/');
      setOrders(status === 200 && Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
    const onRefresh = () => loadOrders();
    window.addEventListener("orders:refresh", onRefresh);
    return () => window.removeEventListener("orders:refresh", onRefresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- Производные данные ---------- */
  const statusOptions = useMemo(() => {
    const uniq = new Map();
    orders.forEach((o) => {
      const code = (o?.status?.code || o?.status?.title || "").toString();
      if (!code) return;
      const title = o?.status?.title || o?.status?.code || code;
      uniq.set(code, title);
    });
    return [{ code: "", title: "Все статусы" }, ...Array.from(uniq, ([code, title]) => ({ code, title }))];
  }, [orders]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return orders.filter((o) => {
      const byStatus = statusFilter ? (o?.status?.code === statusFilter || o?.status?.title === statusFilter) : true;
      if (!byStatus) return false;
      if (!q) return true;
      const fio = `${o.first_name || ""} ${o.last_name || ""}`.toLowerCase();
      const number = (o.order_number || "").toString().toLowerCase();
      const email = (o.email || "").toLowerCase();
      const phone = (o.phone || "").toLowerCase();
      return fio.includes(q) || number.includes(q) || email.includes(q) || phone.includes(q);
    });
  }, [orders, query, statusFilter]);

  /* ---------- Ветка деталей ---------- */
  if (selectedOrder) {
    return (
      <AdminPanelOrdersDetails
        orderId={selectedOrder}
        setView={() => setSelectedOrder(null)}
      />
    );
  }

  /* ---------- Разметка списка ---------- */
  return (
    <section className="space-y-4">
      {/* Тулбар */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-xl font-bold">Заказы</h2>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="relative">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Поиск: №, ФИО, email, телефон"
              className="w-72 max-w-full rounded-md border border-gray-300 px-3 py-2 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">⌘K</span>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
          >
            {statusOptions.map((s) => (
              <option key={s.code} value={s.code}>{s.title}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={loadOrders}
            className="inline-flex items-center gap-2 rounded-md bg-white ring-1 ring-gray-300 px-3 py-2 text-sm hover:bg-gray-50"
            title="Обновить список"
          >
            {loading ? <Spinner /> : <span className="inline-block">↻</span>}
            Обновить
          </button>
        </div>
      </div>

      {/* Счётчик */}
      <div className="text-sm text-gray-500">
        Найдено: <span className="font-medium text-gray-700">{filtered.length}</span>
      </div>

      {/* Мобильный вид — карточки */}
      <div className="grid grid-cols-1 gap-3 sm:hidden">
        {loading
          ? Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="p-4 rounded-xl ring-1 ring-gray-200 bg-white shadow-sm">
                <div className="h-4 w-40 bg-gray-200 rounded animate-pulse" />
                <div className="mt-2 h-3 w-28 bg-gray-200 rounded animate-pulse" />
                <div className="mt-2 h-3 w-56 bg-gray-200 rounded animate-pulse" />
                <div className="mt-4 h-9 w-full bg-gray-200 rounded animate-pulse" />
              </div>
            ))
          : filtered.map((o) => {
              const items = o.items || o.positions || o.order_items || [];
              const qty = Array.isArray(items) ? sumSafe(items, (x) => x.quantity ?? x.qty ?? 0) : (o.total_quantity ?? 0);
              const amount =
                o.total_amount ?? o.total_price ??
                (Array.isArray(items) ? sumSafe(items, (x) => (x.price ?? x.unit_price ?? 0) * (x.quantity ?? x.qty ?? 0)) : 0);

              return (
                <div key={o.id} className="p-4 rounded-xl ring-1 ring-gray-200 bg-white shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm text-gray-500">Заказ</div>
                      <div className="font-semibold truncate">№ {o.order_number || o.id}</div>
                    </div>
                    <StatusBadge status={o.status} />
                  </div>

                  <div className="mt-2 text-sm text-gray-700 truncate">
                    {o.first_name} {o.last_name} · {o.email || "—"}
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    Создан: {fmtDate(getCreated(o))}
                  </div>

                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                    <div className="text-gray-600">Позиций: <span className="font-semibold text-gray-800">{qty || 0}</span></div>
                    <div className="text-gray-600">Сумма: <span className="font-semibold text-gray-800">
                      {Number(amount || 0).toLocaleString("ru-RU")} ₽
                    </span></div>
                  </div>

                  <button
                    onClick={() => setSelectedOrder(o.id)}
                    className="mt-4 w-full inline-flex items-center justify-center rounded-md bg-orange-500 px-4 py-2 text-white text-sm font-semibold shadow hover:bg-orange-600 active:translate-y-[1px] transition"
                  >
                    Подробнее
                  </button>
                </div>
              );
            })
        }

        {!loading && filtered.length === 0 && (
          <div className="p-6 text-center text-gray-500 ring-1 ring-gray-200 rounded-xl bg-white">
            Заказов не найдено. Измените параметры поиска/фильтра.
          </div>
        )}
      </div>

      {/* Десктопный вид — таблица */}
      <div className="hidden sm:block overflow-x-auto rounded-xl ring-1 ring-gray-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="p-3 font-semibold">ID</th>
              <th className="p-3 font-semibold">Номер</th>
              <th className="p-3 font-semibold">Покупатель</th>
              <th className="p-3 font-semibold">E-mail</th>
              <th className="p-3 font-semibold">Создан</th>
              <th className="p-3 font-semibold">Статус</th>
              <th className="p-3 font-semibold">Действие</th>
            </tr>
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 7 }).map((__, j) => (
                      <td key={j} className="p-3"><div className="h-4 w-full bg-gray-200 rounded animate-pulse" /></td>
                    ))}
                  </tr>
                ))
              : filtered.map((o) => (
                  <tr key={o.id} className="border-t last:border-b">
                    <td className="p-3">{o.id}</td>
                    <td className="p-3 font-medium">№ {o.order_number || o.id}</td>
                    <td className="p-3">
                      <div className="min-w-0">
                        <div className="truncate">{o.first_name} {o.last_name}</div>
                        <div className="text-xs text-gray-500 truncate">{o.phone || "—"}</div>
                      </div>
                    </td>
                    <td className="p-3">{o.email}</td>
                    <td className="p-3">{fmtDate(getCreated(o))}</td>
                    <td className="p-3"><StatusBadge status={o.status} /></td>
                    <td className="p-3">
                      <button
                        onClick={() => setSelectedOrder(o.id)}
                        className="inline-flex items-center rounded-md bg-white ring-1 ring-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50"
                      >
                        Подробнее →
                      </button>
                    </td>
                  </tr>
                ))
            }
          </tbody>
        </table>

        {!loading && filtered.length === 0 && (
          <div className="p-6 text-center text-gray-500">Заказов не найдено. Попробуйте изменить фильтры.</div>
        )}
      </div>
    </section>
  );
}
